import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import HeaderThin from 'app/components/layout/HeaderThin';
import SectionGroup from 'app/components/general/SectionGroup';
import SchemaForm from 'app/components/general/SchemaForm';

import * as competitionActions from 'app/ducks/competitions/actions';
import * as loadingSelectors from 'app/ducks/loading/selectors';
import * as organisationSelectors from 'app/ducks/organisations/selectors';
import * as competitionSelectors from 'app/ducks/competitions/selectors';

const formSchema = {
  type: 'object',
  required: ['name', 'friendlyCode'],
  properties: {
    name: {
      type: 'string',
      title: 'Name',
    },
    friendlyCode: {
      type: 'string',
      title: 'Code (short competition code/acronym)',
      minLength: 2,
    },
    seasonName: {
      type: 'string',
      title: 'Season',
    },
    seasonStartAt: {
      type: 'array',
      items: {
        type: 'string',
      },
      enableTime: false,
      inputType: 'datetime',
      title: 'Start Date',
    },
    seasonEndAt: {
      type: 'array',
      items: {
        type: 'string',
      },
      enableTime: false,
      inputType: 'datetime',
      title: 'End Date',
    },
    externalSystemName: {
      type: 'string',
      title: 'Competition Name (In fixture system)',
    },
  },
};

const uiSchema = {
  notificationType: { 'ui:field': 'select' },
  seasonStartAt: { 'ui:field': 'datetime' },
  seasonEndAt: { 'ui:field': 'datetime' },
};

interface ICompetitionCreateProps {
  appReady: boolean;
  actions: any;
  match: any;
  organisations: any;
  competitions: any;
  errors: any;
}

interface ICompetitionCreateState {
  originalFormData: any;
  formData: any;
  changed: boolean;
  saved: boolean;
  errors: any;
}

const selector = (state): any => ({
  organisations: organisationSelectors.getOrganisations(state),
  competitions: competitionSelectors.getCompetitions(state),
  appReady: loadingSelectors.appReady(state),
  errors: {
    ...state.notificationConfigs.backendErrors,
  },
});

const actionCreators = _.merge(competitionActions);

@connect(selector, (dispatch) => ({
  actions: bindActionCreators(actionCreators, dispatch),
}))
export default class CompetitionCreate extends React.Component<
  ICompetitionCreateProps,
  ICompetitionCreateState
> {
  static defaultProps: ICompetitionCreateProps = {
    appReady: false,
    actions: undefined,
    match: undefined,
    organisations: undefined,
    competitions: undefined,
    errors: undefined,
  };

  state: ICompetitionCreateState = {
    originalFormData: {},
    formData: {
      name: undefined,
      friendlyCode: undefined,
      seasonName: undefined,
      externalSystemName: undefined,
      seasonStartAt: [],
      seasonEndAt: [],
    },
    changed: false,
    saved: false,
    errors: {},
  };

  static getDerivedStateFromProps(nextProps, prevState): any {
    if (!prevState.changed) {
      return {
        ...prevState,
        errors: nextProps.errors,
      };
    }
    return null;
  }

  isValid = (): boolean => _.isEmpty(this.state.errors) && this.state.changed;

  handleChange = (e): void => {
    const changed = this.state.originalFormData !== e.formData;
    this.setState(() => ({
      formData: e.formData,
      errors: [],
      changed,
    }));
  };

  handleError = (errors): void => {
    this.setState({ errors });
  }

  handleSubmit = (e): void => {
    if (this.isValid()) {
      const payload = {
        name: this.state.formData.name,
        friendlyCode: this.state.formData.friendlyCode,
        seasonName: this.state.formData.seasonName,
        externalSystemName: this.state.formData.externalSystemName,
        seasonStartAt: this.state.formData.seasonStartAt[0],
        seasonEndAt: this.state.formData.seasonEndAt[0],
        organisation: {
          id: this.props.organisations.currentOrganisationId,
        },
      };

      this.props.actions.createCompetition(payload);
    }
    this.setState({ changed: false });
  };

  render(): React.ReactNode {
    return (
      <div>
        <div className="row">
          <div className="col">
            <HeaderThin
              title="Create Competition"
              divider
            />
          </div>
        </div>
        <div className="row">
          <div className="col col-12 col-lg-8 col-xlg-6  ">
            <SectionGroup title="Competition Details">
              <SchemaForm
                uiSchema={uiSchema}
                schema={formSchema}
                handleChange={this.handleChange}
                handleError={this.handleError}
                handleSubmit={this.handleSubmit}
                formData={this.state.formData}
                errors={this.props.errors}
                liveValidate={this.state.changed}
                isValid={this.isValid()}
                isLoading={this.props.competitions.isSaving}
                hasSaved={this.props.competitions.hasSaved}
                hasFailed={this.props.competitions.hasFailed}
                originalFormData={this.state.originalFormData}
                buttonText="Create Competition"
              />
            </SectionGroup>
          </div>
        </div>
      </div>
    );
  }
}
